import type {
  Department, DocumentRecord, QRRecord,
  AuditLog, AppNotification,
  DocumentCategory,
} from '../types';

// ── Mock Categories ───────────────────────────────────────────────

export const defaultCategories: DocumentCategory[] = [
  { id: 'cat-books',    nameAr: 'الكتب',        nameEn: 'Books',           icon: 'BookOpen',    color: '#C8891E', custom: false },
  { id: 'cat-reports',  nameAr: 'التقارير',     nameEn: 'Reports',         icon: 'FileBarChart', color: '#3B82F6', custom: false },
  { id: 'cat-minutes',  nameAr: 'المحاضر',      nameEn: 'Minutes',         icon: 'ClipboardList',color: '#10B981', custom: false },
  { id: 'cat-records',  nameAr: 'السجلات',      nameEn: 'Records',         icon: 'Archive',     color: '#8B5CF6', custom: false },
  { id: 'cat-letters',  nameAr: 'المراسلات',    nameEn: 'Correspondence',  icon: 'Mail',        color: '#F59E0B', custom: false },
];

// ── Mock Departments ──────────────────────────────────────────────

export const mockDepartments: Department[] = [
  {
    id: 'dept-storage',
    slug: 'al-makhazin',
    nameAr: 'المخازن',
    nameEn: 'Storage',
    descAr: 'إدارة مخازن الأدوية والمستلزمات الصيدلانية',
    descEn: 'Pharmaceutical storage management',
    icon: 'Warehouse',
    code: 'PH-ST',
    theme: {
      primaryColor: '#B8720A',
      secondaryColor: '#020C1B',
      accentColor:  '#D4A030',
      darkMode: true,
    },
    layout: {
      mobileWidgets: [
        { id: 'w1', type: 'stats_card',     titleAr: 'الإحصائيات',   titleEn: 'Statistics', icon: 'BarChart3', size: 'full',   order: 0, visible: true, locked: false, config: {} },
        { id: 'w2', type: 'search_bar',     titleAr: 'البحث',         titleEn: 'Search',     icon: 'Search',    size: 'full',   order: 1, visible: true, locked: false, config: {} },
        { id: 'w3', type: 'quick_actions',  titleAr: 'إجراءات سريعة', titleEn: 'Quick',     icon: 'Zap',       size: 'full',   order: 2, visible: true, locked: false, config: {} },
        { id: 'w4', type: 'document_list',  titleAr: 'المستندات',     titleEn: 'Documents', icon: 'Files',     size: 'full',   order: 3, visible: true, locked: false, config: {} },
        { id: 'w5', type: 'storage_meter',  titleAr: 'التخزين',       titleEn: 'Storage',   icon: 'HardDrive', size: 'medium', order: 4, visible: true, locked: false, config: {} },
        { id: 'w6', type: 'drive_status',   titleAr: 'Drive',         titleEn: 'Drive',     icon: 'Cloud',     size: 'medium', order: 5, visible: true, locked: false, config: {} },
      ],
      desktopWidgets: [],
    },
    managerId: 'user-admin',
    members: ['user-admin', 'user-emp1', 'user-emp2'],
    categories: defaultCategories,
    drive: { id: 'drv-1', departmentId: 'dept-storage', email: '', status: 'disconnected', folderId: '', folderName: '' },
    quota: { usedBytes: 48_500_000, totalBytes: 16_106_127_360, defaultMaxBytes: 512_000, exceptionMaxBytes: 2_097_152, warningThreshold: 0.8 },
    status: 'active',
    createdAt: '2026-01-01T08:00:00Z',
    updatedAt: '2026-06-15T10:30:00Z',
    documentCount: 184,
  },
  {
    id: 'dept-pharmacy',
    slug: 'al-saydaliyat',
    nameAr: 'الصيدليات',
    nameEn: 'Pharmacies',
    descAr: 'إدارة الصيدليات الميدانية',
    descEn: 'Field pharmacies management',
    icon: 'Pill',
    code: 'PH-PH',
    theme: { primaryColor: '#1E3569', secondaryColor: '#020C1B', accentColor: '#3B82F6', darkMode: true },
    layout: { mobileWidgets: [], desktopWidgets: [] },
    managerId: 'user-mgr2',
    members: ['user-mgr2', 'user-emp3'],
    categories: defaultCategories,
    drive: { id: 'drv-2', departmentId: 'dept-pharmacy', email: '', status: 'disconnected' },
    quota: { usedBytes: 22_100_000, totalBytes: 16_106_127_360, defaultMaxBytes: 512_000, exceptionMaxBytes: 2_097_152, warningThreshold: 0.8 },
    status: 'active',
    createdAt: '2026-01-05T09:00:00Z',
    updatedAt: '2026-05-20T14:00:00Z',
    documentCount: 97,
  },
  {
    id: 'dept-control',
    slug: 'al-riqaba',
    nameAr: 'الرقابة الدوائية',
    nameEn: 'Drug Control',
    descAr: 'رقابة الأدوية وضمان الجودة',
    descEn: 'Drug monitoring and quality assurance',
    icon: 'ShieldCheck',
    code: 'PH-DC',
    theme: { primaryColor: '#065F46', secondaryColor: '#020C1B', accentColor: '#10B981', darkMode: true },
    layout: { mobileWidgets: [], desktopWidgets: [] },
    managerId: 'user-admin',
    members: ['user-admin'],
    categories: defaultCategories,
    drive: { id: 'drv-3', departmentId: 'dept-control', email: '', status: 'disconnected' },
    quota: { usedBytes: 9_800_000, totalBytes: 16_106_127_360, defaultMaxBytes: 512_000, exceptionMaxBytes: 2_097_152, warningThreshold: 0.8 },
    status: 'active',
    createdAt: '2026-02-01T08:00:00Z',
    updatedAt: '2026-06-01T09:00:00Z',
    documentCount: 56,
  },
  {
    id: 'dept-stats',
    slug: 'al-ihsaa',
    nameAr: 'الإحصاء',
    nameEn: 'Statistics',
    descAr: 'الإحصاء والتقارير الدورية',
    descEn: 'Statistics and periodic reports',
    icon: 'BarChart2',
    code: 'PH-STAT',
    theme: { primaryColor: '#7C3AED', secondaryColor: '#020C1B', accentColor: '#A78BFA', darkMode: true },
    layout: { mobileWidgets: [], desktopWidgets: [] },
    managerId: 'user-admin',
    members: ['user-admin', 'user-emp4'],
    categories: defaultCategories,
    drive: { id: 'drv-4', departmentId: 'dept-stats', email: '', status: 'disconnected' },
    quota: { usedBytes: 5_200_000, totalBytes: 16_106_127_360, defaultMaxBytes: 512_000, exceptionMaxBytes: 2_097_152, warningThreshold: 0.8 },
    status: 'active',
    createdAt: '2026-02-10T10:00:00Z',
    updatedAt: '2026-06-10T08:00:00Z',
    documentCount: 214,
  },
  {
    id: 'dept-clinical',
    slug: 'al-saydalah-alsarireyah',
    nameAr: 'الصيدلة السريرية',
    nameEn: 'Clinical Pharmacy',
    descAr: 'الصيدلة السريرية ومتابعة المرضى دوائياً',
    descEn: 'Clinical pharmacy and medication management',
    icon: 'Stethoscope',
    code: 'PH-CLN',
    theme: { primaryColor: '#BE123C', secondaryColor: '#020C1B', accentColor: '#FB7185', darkMode: true },
    layout: { mobileWidgets: [], desktopWidgets: [] },
    managerId: 'user-mgr3',
    members: ['user-mgr3'],
    categories: defaultCategories,
    drive: { id: 'drv-5', departmentId: 'dept-clinical', email: '', status: 'disconnected' },
    quota: { usedBytes: 3_100_000, totalBytes: 16_106_127_360, defaultMaxBytes: 512_000, exceptionMaxBytes: 2_097_152, warningThreshold: 0.8 },
    status: 'active',
    createdAt: '2026-03-01T11:00:00Z',
    updatedAt: '2026-06-05T13:00:00Z',
    documentCount: 214,
  },
];

// ── Mock Documents ────────────────────────────────────────────────

export const mockDocuments: DocumentRecord[] = [
  {
    id: 'doc-001',
    archiveId: 'PH-ST-2026-000184',
    bookNumber: '184',
    nameAr: 'كتاب تجهيز أدوية رقم 184',
    nameEn: 'Drug Supply Book No. 184',
    departmentId: 'dept-storage',
    departmentSlug: 'al-makhazin',
    category: 'cat-books',
    confidentiality: 'internal',
    status: 'active',
    fileType: 'pdf',
    fileSizeBytes: 1_280_000,
    compressedBytes: 496_000,
    pageCount: 12,
    uploadedBy: 'user-admin',
    uploadedAt: '2026-06-10T09:15:00Z',
    updatedAt:  '2026-06-10T09:20:00Z',
    tags: ['تجهيز', 'أدوية', '2026'],
    versions: [],
    qrId: 'qr-001',
    notes: '',
  },
  {
    id: 'doc-002',
    archiveId: 'PH-ST-2026-000209',
    bookNumber: '209',
    nameAr: 'محضر استلام رقم 209',
    nameEn: 'Receipt Minute No. 209',
    departmentId: 'dept-storage',
    departmentSlug: 'al-makhazin',
    category: 'cat-minutes',
    confidentiality: 'internal',
    status: 'active',
    fileType: 'pdf',
    fileSizeBytes: 890_000,
    compressedBytes: 320_000,
    pageCount: 4,
    uploadedBy: 'user-emp1',
    uploadedAt: '2026-06-12T11:00:00Z',
    updatedAt:  '2026-06-12T11:05:00Z',
    tags: ['محضر', 'استلام'],
    versions: [],
    qrId: 'qr-002',
    notes: '',
  },
  {
    id: 'doc-003',
    archiveId: 'PH-ST-2026-000210',
    nameAr: 'تقرير شهري يونيو 2026',
    nameEn: 'Monthly Report June 2026',
    departmentId: 'dept-storage',
    departmentSlug: 'al-makhazin',
    category: 'cat-reports',
    confidentiality: 'confidential',
    status: 'active',
    fileType: 'pdf',
    fileSizeBytes: 2_100_000,
    compressedBytes: 1_900_000,
    pageCount: 22,
    uploadedBy: 'user-admin',
    uploadedAt: '2026-07-01T08:30:00Z',
    updatedAt:  '2026-07-01T08:45:00Z',
    tags: ['تقرير', 'شهري', 'يونيو'],
    versions: [],
    qrId: 'qr-003',
    notes: 'حجم الملف يتجاوز الحد الافتراضي — تمت الموافقة على استثناء',
  },
  {
    id: 'doc-004',
    archiveId: 'PH-ST-2026-000211',
    nameAr: 'سجل درجات الحرارة',
    nameEn: 'Temperature Log',
    departmentId: 'dept-storage',
    departmentSlug: 'al-makhazin',
    category: 'cat-records',
    confidentiality: 'internal',
    status: 'active',
    fileType: 'pdf',
    fileSizeBytes: 245_000,
    compressedBytes: 198_000,
    pageCount: 8,
    uploadedBy: 'user-emp2',
    uploadedAt: '2026-07-05T07:00:00Z',
    updatedAt:  '2026-07-05T07:05:00Z',
    tags: ['سجل', 'حرارة'],
    versions: [],
    qrId: 'qr-004',
  },
  {
    id: 'doc-005',
    archiveId: 'PH-ST-2026-000212',
    nameAr: 'كتاب احتياج أدوية',
    nameEn: 'Drug Need Request',
    departmentId: 'dept-storage',
    departmentSlug: 'al-makhazin',
    category: 'cat-books',
    confidentiality: 'internal',
    status: 'disabled',
    fileType: 'pdf',
    fileSizeBytes: 380_000,
    compressedBytes: 298_000,
    pageCount: 6,
    uploadedBy: 'user-emp1',
    uploadedAt: '2026-06-28T14:00:00Z',
    updatedAt:  '2026-07-03T09:00:00Z',
    tags: ['احتياج', 'أدوية'],
    versions: [],
    qrId: 'qr-005',
  },
  {
    id: 'doc-006',
    archiveId: 'PH-CLN-2026-000214',
    nameAr: 'أمر توزيع دوائي',
    nameEn: 'Drug Distribution Order',
    departmentId: 'dept-clinical',
    departmentSlug: 'al-saydalah-alsarireyah',
    category: 'cat-letters',
    confidentiality: 'confidential',
    status: 'active',
    fileType: 'pdf',
    fileSizeBytes: 150_000,
    compressedBytes: 120_000,
    pageCount: 2,
    uploadedBy: 'user-mgr3',
    uploadedAt: '2026-07-08T10:00:00Z',
    updatedAt:  '2026-07-08T10:03:00Z',
    tags: ['توزيع', 'دوائي'],
    versions: [],
    qrId: 'qr-006',
  },
];

// ── Mock QR Records ───────────────────────────────────────────────

export const mockQRRecords: QRRecord[] = mockDocuments.map((doc, i) => ({
  id:           `qr-00${i + 1}`,
  token:        `MASAR-${doc.archiveId}-TOKEN`,
  documentId:   doc.id,
  departmentId: doc.departmentId,
  status:       doc.status === 'disabled' ? 'disabled' : 'active',
  resolvedUrl:  `/documents/${doc.id}`,
  createdAt:    doc.uploadedAt,
  updatedAt:    doc.updatedAt,
  scanCount:    Math.floor(Math.random() * 30),
  lastScannedAt: i < 3 ? '2026-07-10T08:00:00Z' : undefined,
}));

// ── Mock Audit Logs ───────────────────────────────────────────────

export const mockAuditLogs: AuditLog[] = [
  { id: 'log-001', userId: 'user-admin', userNameAr: 'عبدالله جواد', action: 'document.upload', targetId: 'doc-001', targetType: 'document', departmentId: 'dept-storage', createdAt: '2026-06-10T09:15:00Z' },
  { id: 'log-002', userId: 'user-emp1',  userNameAr: 'أحمد محمد',    action: 'document.upload', targetId: 'doc-002', targetType: 'document', departmentId: 'dept-storage', createdAt: '2026-06-12T11:00:00Z' },
  { id: 'log-003', userId: 'user-admin', userNameAr: 'عبدالله جواد', action: 'qr.disable',     targetId: 'qr-005',  targetType: 'qr',       departmentId: 'dept-storage', createdAt: '2026-07-03T09:00:00Z' },
  { id: 'log-004', userId: 'user-admin', userNameAr: 'عبدالله جواد', action: 'dept.create',    targetId: 'dept-clinical', targetType: 'department', createdAt: '2026-03-01T11:00:00Z' },
];

// ── Mock Notifications ────────────────────────────────────────────

export const mockNotifications: AppNotification[] = [
  {
    id: 'notif-001', type: 'warning',
    titleAr: 'Drive غير مرتبط',    titleEn: 'Drive Not Connected',
    bodyAr:  'شعبة المخازن لم تربط حساب Google Drive بعد.',
    bodyEn:  'Storage department has not connected a Google Drive account yet.',
    read: false, createdAt: '2026-07-10T08:00:00Z', linkRoute: '/admin',
  },
  {
    id: 'notif-002', type: 'info',
    titleAr: 'مستند جديد',   titleEn: 'New Document',
    bodyAr:  'تم رفع محضر استلام رقم 209 بنجاح.',
    bodyEn:  'Receipt Minute No. 209 uploaded successfully.',
    read: true,  createdAt: '2026-06-12T11:05:00Z', linkRoute: '/documents/doc-002',
  },
];
